import { motion } from "framer-motion";
import { Mail, Phone, Instagram, X, Facebook } from "lucide-react";
import logo from "@/assets/images/Logo.png";
import { useNavigate } from "react-router-dom";

const Footer: React.FC = () => {
  const navigate = useNavigate();

  const socialLinks = [
    { icon: <Instagram size={18} />, href: "#" },
    { icon: <X size={18} />, href: "#" },
    { icon: <Facebook size={18} />, href: "#" },
  ];

  const handleNavigation = (btn: string) => {
    if (btn === "Customer") {
      navigate("/customer-landing");
    } else if (btn === "Business") {
      navigate("/business");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white pt-12 pb-6 px-6 md:px-20 overflow-hidden">
      {/* Top Buttons */}
      <motion.div
        className="flex justify-center gap-6 flex-wrap mb-12"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true }}
      >
        {["Customer", "Business"].map((btn, i) => (
          <motion.button
            key={i}
            onClick={() => handleNavigation(btn)}
            whileHover={{
              scale: 1.05,
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              borderColor: "white",
            }}
            transition={{ type: "spring", stiffness: 200 }}
            className="border-2 border-white/60 rounded-full px-8 py-3 font-semibold text-sm transition-colors duration-300 hover:text-white"
          >
            {btn}
          </motion.button>
        ))}
      </motion.div>

      <hr className="border-white/20 mb-12" />

      {/* Footer Grid */}
      <div className="grid md:grid-cols-3 gap-10 items-start">
        {/* Left section - Logo */}
        <motion.div
          className="flex flex-col items-center text-center space-y-1"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <motion.img
            src={logo}
            alt="Smartly Orders Logo"
            className="w-40 h-32 object-contain"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 150 }}
          />
          <p className="text-sm font-bold tracking-widest text-teal-50 uppercase">
            SMARTLY ORDERS
          </p>
        </motion.div>

        {/* Middle section - Contact Info */}
        <motion.div
          className="text-center md:text-left space-y-4"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <h3 className="text-sm font-bold uppercase tracking-widest text-teal-150 mb-4 text-teal-100">
            Get in Touch
          </h3>

          <div className="flex items-center justify-center md:justify-start gap-2 text-white hover:text-teal-100 transition-colors">
            <Mail size={16} />
            <a
              href="mailto:customersupport@smartlyorders.com"
              className="text-sm hover:underline break-all"
            >
              customersupport@smartlyorders.com
            </a>
          </div>

          <div className="flex items-center justify-center md:justify-start gap-2 text-white hover:text-teal-100 transition-colors">
            <Phone size={16} />
            <a href="tel:+919874563210" className="text-sm hover:underline">
              +91 9874563210
            </a>
          </div>

          {/* Social Icons */}
          <div className="flex justify-center md:justify-start gap-3 mt-5">
            {socialLinks.map((item, i) => (
              <motion.a
                key={i}
                href={item.href}
                className="w-9 h-9 border border-white/40 rounded-xl flex items-center justify-center hover:bg-white hover:text-emerald-600 transition-all duration-300"
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                {item.icon}
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Right section - Links */}
        <motion.div
          className="flex flex-col text-center md:text-left space-y-3 text-teal-100"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <h3 className="text-sm font-bold uppercase tracking-widest text-teal-100 mb-2">
            Navigation
          </h3>
          {[
            { name: "Home", link: "/" },
            { name: "Customer", link: "/customer-landing" },
            { name: "Business", link: "/business" },
          ].map((item, i) => (
            <motion.a
              key={i}
              href={item.link}
              className="inline-block text-sm text-teal-50 hover:text-white transition-colors duration-200"
              whileHover={{ x: 6 }}
              transition={{ type: "spring", stiffness: 250 }}
            >
              {item.name}
            </motion.a>
          ))}
        </motion.div>
      </div>

      {/* Footer Note */}
      <motion.div
        className="flex flex-col md:flex-row justify-between items-center text-xs text-teal-100 mt-10 mb-5 px-5 sm:px-10 gap-4 border-t border-white/20 pt-6"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        viewport={{ once: true }}
      >
        <div className="text-center md:text-left space-y-1">
          <p>
            © 2026 smartlyorder.com Operated by Taphubsglobal IT solutions and
            Services PVT LTD.{" "}
            <a
              href="https://www.taphubs.com/disclaimer"
              className="text-white hover:underline font-semibold"
            >
              Disclaimer
            </a>
          </p>
          <p>CIN: U62090KL2024PTC089716</p>
        </div>

        <div className="flex flex-wrap justify-center md:justify-end gap-6 items-center text-teal-100">
          <a
            href="https://taphubs.org/masteragreement"
            className="hover:text-white transition-colors"
          >
            Master Agreement
          </a>
          <a
            href="https://www.taphubs.com/privacy"
            className="hover:text-white transition-colors"
          >
            Privacy Policy
          </a>
          <a
            href="https://www.taphubs.com/terms&service"
            className="hover:text-white transition-colors"
          >
            Terms of Service
          </a>
          <span className="text-teal-200 font-mono font-bold">
            Version 4.0.0
          </span>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;
